import type { Api } from "../api";
import { empty, h, label, pill, sectionHead } from "../dom";
import { longDate, money, qty, shortDate, when } from "../format";
import { plain } from "../words";
import { can, prepareEmailButton } from "./shared";
import { approvals, approvedNotSent, byUrgency, documentsToReview, lede } from "../home";
import { inquiryState } from "../inquiries";
import { entryState, kindWords } from "../quickbooks";
import type { Draft, Exception, InboxMessage, Inquiry, Me, Order, Page, QbEntry, VendorPoRow } from "../types";

// Home, "what needs you" (D-23): vendor POs awaiting approval, drafts to send, documents the reader wasn't sure of,
// and open exceptions; open orders for the tiles. Documents from senders Tare doesn't know are only counted, with a link
// to their own pile (0077-0078); website inquiries that weren't emailed to sales@ are listed. All asked at once.

function daysAgo(n: number): string {
  return new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);
}

const URGENCY_TONE: Record<string, "late" | "soon" | "neutral"> = { CRITICAL: "late", ELEVATED: "soon", ROUTINE: "neutral" };

export async function home(api: Api, me: Me): Promise<HTMLElement> {
  const [orders, vendorPos, drafts, inbox, exceptions, unknown, inq, qb] = await Promise.all([
    api.tool<Page<Order>>("find_orders", { status: "OPEN", limit: 200 }),
    // open (not closed) Tare POs, awaiting approval first: feeds both "to approve" and "approved, not sent"
    api.tool<Page<VendorPoRow>>("vendor_pos", { status: "OPEN", kind: "TARE_PO", limit: 200 }),
    api.tool<Page<Draft>>("outbound_drafts", { status: "PENDING", limit: 100 }),
    api.tool<Page<InboxMessage>>("inbox", { status: "NEEDS_REVIEW", sender: "KNOWN", since: daysAgo(30), limit: 100 }),
    api.tool<Page<Exception>>("exceptions", { limit: 100 }),
    api.tool<Page<InboxMessage>>("inbox", { status: "NEEDS_REVIEW", sender: "UNKNOWN", since: daysAgo(30), limit: 100 })
      .catch(() => null),
    can(me, "orders.read") ? api.tool<Page<Inquiry>>("inquiries", { since: daysAgo(30), limit: 100 }).catch(() => null) : Promise.resolve(null),
    can(me, "invoices.read") && can(me, "costs.read")
      ? api.tool<Page<QbEntry>>("qb_entries", { status: "OPEN", limit: 100 }).catch(() => null) : Promise.resolve(null),
  ]);
  const qbToDecide = (qb?.rows ?? []).filter((e) => e.status === "PROPOSED" || e.status === "FAILED");
  const unknownDocs = unknown ? documentsToReview(unknown.rows).length : 0;
  const unanswered = (inq?.rows ?? []).filter((r) => inquiryState(r.status).needsReply);
  const toApprove = approvals(vendorPos.rows);
  const notSent = approvedNotSent(vendorPos.rows, drafts.rows);
  const canPrepare = can(me, "drafts.write");
  const notSentReport = h("div", { class: "result", "aria-live": "polite" });
  const atCost = toApprove.every((a) => typeof a.costValue === "number"); // absent without costs.read
  const docs = documentsToReview(inbox.rows);
  const open = byUrgency(exceptions.rows);
  const canApprove = can(me, "vendor_pos.approve");
  const critical = open.filter((e) => e.urgency === "CRITICAL").length;
  const openValue = orders.rows.reduce((s, o) => s + (o.totals?.sellValue ?? 0), 0);

  const tile = (name: string, figure: number, note: string) =>
    h("div", { class: "tile" }, label(name), h("span", { class: "number-lg" }, figure), h("span", { class: "caption" }, note));

  return h("div", { class: "page" },
    h("section", { class: "stack", "aria-labelledby": "today" },
      h("div", { class: "title-row" },
        h("h1", { id: "today", class: "title" }, longDate(new Date())),
        me.mode === "SIMULATION"
          ? h("p", { class: "aside" }, "Simulation copies. Nothing here reaches a customer or supplier.")
          : null),
      h("p", { class: "lede" }, lede({ approvals: toApprove.length, approvedNotSent: notSent.length, drafts: drafts.rows.length, documents: docs.length,
        exceptions: open.length, critical }, canApprove)),
      h("div", { class: "tiles" },
        tile("Open orders", orders.rows.length, `${money(openValue)} at sell`),
        tile("To approve", toApprove.length, "vendor POs"),
        tile("Drafts", drafts.rows.length, "emails to check and send"),
        tile("Exceptions", open.length, critical ? `${critical} critical` : "open"))),

    h("section", { class: "stack", "aria-labelledby": "approve" },
      sectionHead("approve", "Vendor POs to approve", canApprove ? undefined : "Approving is Josh's; you see these read-only"),
      toApprove.length
        ? h("div", { class: "panel scroll" }, h("table", { class: "tbl" },
            h("thead", null, h("tr", null,
              h("th", { scope: "col" }, "Vendor PO"), h("th", { scope: "col" }, "Supplier"), h("th", { scope: "col" }, "For"),
              h("th", { scope: "col" }, "Ordered"), h("th", { scope: "col", class: "r" }, "Qty EA"),
              atCost ? h("th", { scope: "col", class: "r" }, "At cost") : null,
              h("th", { scope: "col" }, h("span", { class: "visually-hidden" }, "Action")))),
            h("tbody", null, toApprove.map((a) => h("tr", null,
              h("td", { class: "num strong" }, a.number),
              h("td", null, a.supplier?.name ?? ""),
              h("td", null, `${a.customer?.name ?? ""} `, h("a", { class: "num", href: `/order/${a.customerPoId}` }, a.poNumber ?? "")),
              h("td", { class: "num" }, shortDate(a.orderedOn)),
              h("td", { class: "num r" }, qty(a.qtyEa, "")),
              atCost ? h("td", { class: "num r" }, money(a.costValue)) : null,
              h("td", { class: "r" }, h("a", { class: "button button-small", href: `/vendor-po/${a.customerPoId}/${a.vendorPoId}` }, "Review")))))))
        : empty("Nothing to approve", "Vendor POs the platform drafts and checks against the supplier's terms land here.")),

    notSent.length
      ? h("section", { class: "stack", "aria-labelledby": "not-sent" },
          sectionHead("not-sent", "Approved, not sent yet", "Each goes to its supplier when its email is sent"),
          h("div", { class: "panel scroll" }, h("table", { class: "tbl" },
            h("thead", null, h("tr", null,
              h("th", { scope: "col" }, "Vendor PO"), h("th", { scope: "col" }, "Supplier"), h("th", { scope: "col" }, "For"),
              h("th", { scope: "col" }, "Approved"), h("th", { scope: "col" }, "Next"))),
            h("tbody", null, notSent.map(({ vendorPo: v, draftId }) => h("tr", null,
              h("td", { class: "num strong" }, h("a", { href: `/vendor-po/${v.customerPoId}/${v.vendorPoId}` }, v.number)),
              h("td", null, v.supplier?.name ?? ""),
              h("td", null, `${v.customer?.name ?? ""} `, h("a", { class: "num", href: `/order/${v.customerPoId}` }, v.poNumber ?? "")),
              h("td", { class: "num" }, shortDate(v.approvedAt)),
              h("td", null, draftId
                ? h("a", { class: "button button-small button-primary", href: `/draft/${draftId}` }, "Open the email")
                : canPrepare
                  ? h("div", { class: "row-act" }, prepareEmailButton(api, v.vendorPoId, notSentReport, "button-small"), pill("No email prepared", "soon"))
                  : pill("No email prepared", "soon"))))))),
          notSentReport)
      : null,

    h("div", { class: "split" },
      h("section", { class: "stack", "aria-labelledby": "drafts" },
        sectionHead("drafts", "Drafts to send"),
        drafts.rows.length
          ? h("ul", { class: "cards" }, drafts.rows.map((d) => h("li", { class: "card" },
              h("a", { class: "subheading", href: `/draft/${d.draftId}` }, d.final?.subject ?? d.subject),
              h("span", null, `To ${(d.final?.to ?? d.to).join(", ")}`),
              h("span", { class: "caption" }, [d.whyDrafted, when(d.createdAt)].filter(Boolean).join(" · ")))))
          : empty("No drafts", "Emails the platform writes for a person to check, such as a vendor PO to its supplier, land here.")),

      h("section", { class: "stack", "aria-labelledby": "documents" },
        sectionHead("documents", "Documents to review"),
        docs.length
          ? h("ul", { class: "cards" }, docs.map((d) => h("li", { class: "card" },
              h("a", { class: "subheading", href: `/document/${d.documentId}` }, d.filename),
              h("span", null, `${d.subject} · from ${d.from} to ${d.mailbox}@`),
              d.note ? h("span", { class: "caption" }, plain(d.note)) : null,
              h("span", { class: "caption" }, when(d.receivedAt)))))
          : empty("Nothing to review", "Attachments the reader isn't sure of, with its reasons, land here."),
        unknownDocs
          ? h("p", { class: "caption" }, `Also ${unknownDocs} from senders Tare doesn't know: `,
              h("a", { href: "/documents?pile=unknown" }, "look through them"), " (a new customer's first PO arrives there).")
          : null)),

    qbToDecide.length
      ? h("section", { class: "stack", "aria-labelledby": "quickbooks" },
          sectionHead("quickbooks", "QuickBooks: to approve", can(me, "quickbooks.approve") ? "Approve or reject on the QuickBooks screen" : "Approving is an Owner's"),
          h("ul", { class: "cards" }, qbToDecide.map((e) => h("li", { class: "card" },
            h("a", { class: "subheading", href: "/quickbooks" }, `${kindWords(e.kind)}: ${e.party}${e.docNumber ? ` · ${e.docNumber}` : ""}`),
            h("span", { class: "num" }, money(e.amount)),
            h("span", { class: "caption" }, [entryState(e.status).text, e.customerPo ? `PO ${e.customerPo}` : null,
              e.status === "FAILED" && e.lastError ? plain(e.lastError) : null].filter(Boolean).join(" · "))))))
      : null,

    unanswered.length
      ? h("section", { class: "stack", "aria-labelledby": "inquiries" },
          sectionHead("inquiries", "Website inquiries to answer", "Not emailed to sales@: reply from sales@"),
          h("ul", { class: "cards" }, unanswered.map((r) => h("li", { class: "card" },
            h("a", { class: "subheading", href: "/inquiries" }, `${r.name}${r.company ? `, ${r.company}` : ""}`),
            h("span", null, r.message.length > 160 ? `${r.message.slice(0, 160)}…` : r.message),
            h("span", { class: "caption" }, [inquiryState(r.status).text, when(r.receivedAt)].join(" · "))))))
      : null,

    h("section", { class: "stack", "aria-labelledby": "exceptions" },
      sectionHead("exceptions", "Exceptions"),
      open.length
        ? h("div", { class: "panel scroll" }, h("table", { class: "tbl" },
            h("thead", null, h("tr", null,
              h("th", { scope: "col" }, "Urgency"), h("th", { scope: "col" }, "What"), h("th", { scope: "col" }, "Order"),
              h("th", { scope: "col" }, "Blocks"), h("th", { scope: "col" }, "Who has it"))),
            h("tbody", null, open.map((e) => h("tr", null,
              h("td", null, pill(e.urgency.toLowerCase(), URGENCY_TONE[e.urgency] ?? "neutral")),
              h("td", null, h("a", { href: `/exception/${e.exceptionId}` }, e.kindDescription ?? e.kind),
                e.message ? h("div", { class: "caption" }, plain(e.message)) : null),
              h("td", { class: "num" }, [e.party?.name, e.poNumber].filter(Boolean).join(" · ")),
              h("td", null, [e.blocksEvent?.toLowerCase().replace(/_/g, " "), shortDate(e.blocksOn)].filter(Boolean).join(", ")),
              h("td", null, e.assignedTo ?? e.queue.toLowerCase().replace(/_/g, " ")))))))
        : empty("None open", "Each workflow raises what it can't settle, ranked critical, elevated or routine by what it blocks and when.")),
  );
}
